import {
  formatIsoDateItShort,
  formatMonthYear,
  normalizeIsoDate,
  normalizeIsoMonth,
  toIsoMonth,
} from '@/utils/date';

type PlanningUnit = {
  plannedPeriod?: string;
  plannedDate?: string;
  syncDeletedAt?: string | null;
};

export type PlannedInspectionSnapshot = {
  plannedPeriod?: string;
  plannedDate?: string;
  label: string;
  isExact: boolean;
};

const DEFAULT_LABEL = 'Da pianificare';

function formatPlannedLabel(
  plannedPeriod?: string,
  plannedDate?: string
): PlannedInspectionSnapshot {
  const normalizedDate = normalizeIsoDate(plannedDate);
  if (normalizedDate) {
    const period = normalizeIsoMonth(plannedPeriod) ?? toIsoMonth(normalizedDate);
    return {
      plannedDate: normalizedDate,
      plannedPeriod: period,
      label: formatIsoDateItShort(normalizedDate, DEFAULT_LABEL),
      isExact: true,
    };
  }

  const normalizedPeriod = normalizeIsoMonth(plannedPeriod);
  if (normalizedPeriod) {
    return {
      plannedPeriod: normalizedPeriod,
      label: formatMonthYear(normalizedPeriod, 'it-IT'),
      isExact: false,
    };
  }

  return { label: DEFAULT_LABEL, isExact: false };
}

function compareIsoDates(a: string, b: string): number {
  return a.localeCompare(b);
}

export function resolveUnitPlanningSnapshot(
  unit: PlanningUnit | null | undefined
): PlannedInspectionSnapshot {
  if (!unit || unit.syncDeletedAt) {
    return { label: DEFAULT_LABEL, isExact: false };
  }
  return formatPlannedLabel(unit.plannedPeriod, unit.plannedDate);
}

export function resolveUnitsPlanningSnapshot(
  units: PlanningUnit[] | null | undefined
): PlannedInspectionSnapshot {
  const activeUnits = (units ?? []).filter((unit) => !unit.syncDeletedAt);
  if (activeUnits.length === 0) {
    return { label: DEFAULT_LABEL, isExact: false };
  }

  const plannedDateCandidates = activeUnits
    .map((unit) => normalizeIsoDate(unit.plannedDate))
    .filter((value): value is string => Boolean(value))
    .sort(compareIsoDates);

  if (plannedDateCandidates.length > 0) {
    const plannedDate = plannedDateCandidates[0];
    const fallbackPeriod = toIsoMonth(plannedDate);
    const plannedPeriod =
      activeUnits.find((unit) => unit.plannedDate === plannedDate)?.plannedPeriod ?? fallbackPeriod;
    return formatPlannedLabel(plannedPeriod, plannedDate);
  }

  const plannedPeriodCandidates = activeUnits
    .map((unit) => normalizeIsoMonth(unit.plannedPeriod))
    .filter((value): value is string => Boolean(value))
    .sort();

  if (plannedPeriodCandidates.length > 0) {
    return formatPlannedLabel(plannedPeriodCandidates[0]);
  }

  return { label: DEFAULT_LABEL, isExact: false };
}

export function countUnplannedUnits(units: PlanningUnit[] | null | undefined): number {
  return (units ?? []).filter((unit) => !unit.syncDeletedAt && !normalizeIsoDate(unit.plannedDate))
    .length;
}
